import classNames from "classnames"
import { forwardRef, AriaAttributes, useRef, useCallback, MouseEventHandler } from "react"
import { TABLE_CELL_CLASSES } from "./constants"
import { TableHeaderProps } from "./types"
import { Icon } from "../Icon"
import { OysterIcon } from "@oysterhr/icons"
import { useId } from "@floating-ui/react"
import { useTableContext } from "./TableContext"
import { mergeRefs } from "../utils/mergeRefs"
import { useFixedColumnsStyles } from "./useFixedColumnsStyles"

const sortIconMap: Record<"asc" | "desc", OysterIcon> = {
  asc: "CaretUp",
  desc: "CaretDown",
}

const ariaSortAttrMap: Record<"asc" | "desc", AriaAttributes["aria-sort"]> = {
  asc: "ascending",
  desc: "descending",
}

const BASE_TABLE_COLUMN_HEADER_CLASSES =
  "bg-gray-100 shadow-[inset_0px_-2px_0px,inset_0px_1px_0px] h-11 px-4 py-3 text-sm font-medium text-left shadow-gray-200 font-inherit relative [&:first-of-type:has(button)]:pr-2" // Should have pr-2 when Table.SelectAllCheckbox is used

const TableColumnHeaderWithSorting = forwardRef<HTMLTableCellElement, TableHeaderProps>(
  (
    {
      sort,
      className,
      children,
      endAdornment,
      onSortClick: onSortClickProp,
      sortButtonProps,
      sortButtonRef,
      scope,
      ...rest
    },
    ref,
  ) => {
    const { className: fixedColumnsClassName, style } = useFixedColumnsStyles()
    const labelId = useId()
    const isSortActive = typeof sort === "string"
    const _className = classNames(
      BASE_TABLE_COLUMN_HEADER_CLASSES,
      isSortActive && "bg-gray-200",
      fixedColumnsClassName,
      className,
    )

    const onSortClick: MouseEventHandler<HTMLButtonElement> | undefined =
      onSortClickProp &&
      (event => {
        const NEXT_SORT_ORDER_MAP = {
          asc: "desc",
          desc: "asc",
        } as const

        const nextSortOrder = sort ? NEXT_SORT_ORDER_MAP[sort] : "asc"

        onSortClickProp(nextSortOrder, event)
      })

    const sortIconClassName = classNames(isSortActive ? "text-black" : "text-gray-300")
    const sortIcon = (
      <Icon
        size="xs"
        className={sortIconClassName}
        name={isSortActive ? sortIconMap[sort] : "Sort"}
      />
    )
    const cellInner = (
      <div className="flex items-center gap-2">
        <span id={labelId}>{children}</span>
        {endAdornment && (
          // These end adornments are usually used as buttons, links or icons with tooltips
          // So by default stop the event propagation of the end adornment so that it doesn't
          // trigger a sort
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <span onKeyDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
            {endAdornment}
          </span>
        )}
        {sortIcon}
      </div>
    )

    return (
      <th
        aria-sort={isSortActive ? ariaSortAttrMap[sort] : undefined}
        aria-labelledby={labelId}
        className={_className}
        ref={ref}
        scope={scope}
        style={style}
        {...rest}
      >
        {onSortClick && (
          <button
            {...sortButtonProps}
            aria-label={"Sort by " + children}
            aria-pressed={isSortActive}
            className="w-full text-start"
            onClick={onSortClick}
            ref={sortButtonRef}
            type="button"
          >
            {cellInner}
          </button>
        )}
        {!onSortClick && cellInner}
        <Spacer />
      </th>
    )
  },
)

/**
 * Decorative component. Creates a gap in the table header so that the selected message can appear absolutely positioned over the table header
 */
function Spacer() {
  const { afterColumnHeadersHeight: afterColumnHeadersHeight } = useTableContext()

  if (!afterColumnHeadersHeight) {
    return null
  }

  return <div style={{ height: afterColumnHeadersHeight }} />
}

const TableColumnHeader = forwardRef<HTMLTableCellElement, TableHeaderProps>(
  (
    {
      children,
      className,
      endAdornment,
      onSortClick,
      sort,
      scope,
      sortButtonProps,
      sortButtonRef,
      ...rest
    },
    ref,
  ) => {
    const { firstColumnHeaderRef, fixedStartColumn } = useTableContext()
    const { className: fixedColumnsClassName, style } = useFixedColumnsStyles()

    const isFirstChild = useRef(false)
    const labelId = useId()
    const _className = classNames(
      BASE_TABLE_COLUMN_HEADER_CLASSES,
      fixedColumnsClassName,
      className,
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps -- maintain function reference to avoid calling setState too many times
    const headerRef = useCallback(
      mergeRefs(ref, node => {
        if (node?.matches("th:first-child")) {
          isFirstChild.current = true
          firstColumnHeaderRef(node)
        }
        if (!node && isFirstChild.current) {
          firstColumnHeaderRef(node)
        }
      }),
      [fixedStartColumn],
    )
    return (
      <th
        aria-labelledby={labelId}
        scope={scope}
        className={_className}
        ref={headerRef}
        style={style}
        {...rest}
      >
        {!endAdornment && children}
        {endAdornment && (
          <div className="flex items-center gap-2">
            <span id={labelId}>{children}</span>
            {endAdornment}
          </div>
        )}
        <Spacer />
      </th>
    )
  },
)

const TableRowHeader = forwardRef<HTMLTableCellElement, TableHeaderProps>(
  ({ scope, className, children, sort, onSortClick, ...rest }, ref) => {
    const { className: fixedColumnsClassName, style } = useFixedColumnsStyles()
    const _className = classNames(
      "shadow-gray-200 font-inherit items-center",
      TABLE_CELL_CLASSES,
      fixedColumnsClassName,
      className,
    )

    return (
      <th scope={scope} className={_className} ref={ref} style={style} {...rest}>
        {children}
      </th>
    )
  },
)

export const TableHeader = forwardRef<HTMLTableCellElement, TableHeaderProps>((rest, ref) => {
  if ((typeof rest.onSortClick === "function" || rest.sort) && rest.scope === "col") {
    return <TableColumnHeaderWithSorting ref={ref} {...rest} />
  }

  if (rest.scope === "col") {
    return <TableColumnHeader ref={ref} {...rest} />
  }

  return <TableRowHeader ref={ref} {...rest} />
})

TableHeader.displayName = "Table.Header"
